import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import RegisterHeader from "src/features/organization/components/RegisterHeader";
import Button from "src/components/Buttons/Button";
import { ButtonColors } from "src/components/Buttons/buttons.types";
import SquareCheckboxInput from "src/components/Inputs/SquareCheckboxInput";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import useErrorHandling from "src/features/errors/hooks/useErrorHandling";
import legalService from "../services/legalService";
import { useMutation } from "@tanstack/react-query";
import { setEmployeeLegalAgreement } from "src/features/auth/authSlice";
import { useAppDispatch } from "src/store";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

export type AgreementFormValues = z.infer<ReturnType<typeof createAgreementSchema>>;

function createAgreementSchema(t: TFunction<"legal">) {
  return z.object({
    privacy_policy_accepted: z.literal(true, {
      error: () => t("mustAcceptPrivacyPolicy"),
    }),
    cookies_policy_accepted: z.literal(true, {
      error: () => t("mustAcceptCookiesPolicy"),
    }),
    terms_accepted: z.literal(true, {
      error: () => t("mustAcceptTermsOfUse"),
    }),
  });
}

function LegalAgreementsPage() {
  const dispatch = useAppDispatch();
  const handleErrors = useErrorHandling();
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = React.useState("");
  const { t, i18n } = useTranslation("legal");
  const locale = i18n.language || "en";

  const agreementSchema = React.useMemo(() => createAgreementSchema(t), [t]);

  const acceptAgreementMutation = useMutation({
    mutationFn: legalService.acceptAgreements,
    onSuccess: (data) => {
      dispatch(setEmployeeLegalAgreement(data));
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");
      navigate(redirect ? decodeURIComponent(redirect) : "/dashboard");
    },
    onError: handleErrors,
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<AgreementFormValues>({
    resolver: zodResolver(agreementSchema),
  });

  React.useEffect(() => {
    const fetchDoc = (filename: string) =>
      fetch(`/legal/${locale}/${filename}`)
        .then((res) => (res.ok ? res : fetch(`/legal/en/${filename}`)))
        .then((res) => res.text())
        .catch(() => fetch(`/legal/en/${filename}`).then((r) => r.text()));

    Promise.all([
      fetchDoc("privacy.md"),
      fetchDoc("cookies.md"),
      fetchDoc("terms-of-use.md"),
    ]).then(([privacyText, cookiesText, termsText]) => {
      setContent(`${privacyText}\n\n${cookiesText}\n\n${termsText}`);
    });
  }, [locale]);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  const onSubmit = async (data: AgreementFormValues) => {
    acceptAgreementMutation.mutate(data);
  };

  return (
    <div className="flex flex-1 min-h-screen">
      <RegisterHeader />

      <div className="flex flex-1 p-20 gap-20">
        <div className="flex centered border-danger bg-cp-danger-10 border-radius-10">
          <h1 className="text-2xl font-bold p-10 text-center">
            {t("agreementsRequired")}
          </h1>
          <div className="flex-1 pb-20">
            <Button color={ButtonColors.Yellow} onClick={scrollToBottom}>
              {t("scrollToBottom")}
            </Button>
          </div>
        </div>

        <div className="flex-1 border-light-gray p-20 border-radius-10 bg-cp-black-50 overflow-x-auto">
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug]}
            >
              {content}
            </ReactMarkdown>
          </div>
        </div>

        <div className="flex centered border-light-gray p-20 bg-cp-black-50 border-radius-10">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-10"
          >
            <SquareCheckboxInput
              id="privacy_policy_accepted"
              label={t("acceptPrivacyPolicy")}
              {...register("privacy_policy_accepted")}
            />
            <SquareCheckboxInput
              id="cookies_policy_accepted"
              label={t("acceptCookiesPolicy")}
              {...register("cookies_policy_accepted")}
            />
            <SquareCheckboxInput
              id="terms_accepted"
              label={t("acceptTermsOfUse")}
              {...register("terms_accepted")}
            />

            <Button
              type="submit"
              color={ButtonColors.Yellow}
              disabled={!isValid}
            >
              {t("acceptAndContinue")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default React.memo(LegalAgreementsPage);
